// SPDX-License-Identifier: GPL-3.0-only
// Copyright (C) 2026 Kyle Johnson

import Papa from 'papaparse';
import type { ImportResult, ImportedTransaction, ImportError, ImportWarning } from './types.js';
import { getCurrencyDecimalPlaces } from '../currencies.js';

type Row = Record<string, string>;

// ---------------------------------------------------------------------------
// Date parsing
// ---------------------------------------------------------------------------

const DATE_FORMATS = [
  // US: MM/DD/YYYY
  { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, parse: (m: RegExpMatchArray) => `${m[3]}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`, ambiguous: true },
  // ISO: YYYY-MM-DD
  { re: /^(\d{4})-(\d{2})-(\d{2})$/, parse: (m: RegExpMatchArray) => `${m[1]}-${m[2]}-${m[3]}`, ambiguous: false },
  // MM-DD-YYYY
  { re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, parse: (m: RegExpMatchArray) => `${m[3]}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`, ambiguous: true },
  // M/D/YYYY (Mint)
  { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, parse: (m: RegExpMatchArray) => `${m[3]}-${m[1]!.padStart(2, '0')}-${m[2]!.padStart(2, '0')}`, ambiguous: true },
  // MMM DD, YYYY
  { re: /^([A-Za-z]{3})\s+(\d{1,2}),\s*(\d{4})$/, parse: (m: RegExpMatchArray) => {
    const months: Record<string, string> = { jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12' };
    return `${m[3]}-${months[m[1]!.toLowerCase()] ?? '01'}-${m[2]!.padStart(2, '0')}`;
  }, ambiguous: false },
  // DD MMM YYYY
  { re: /^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})$/, parse: (m: RegExpMatchArray) => {
    const months: Record<string, string> = { jan:'01',feb:'02',mar:'03',apr:'04',may:'05',jun:'06',jul:'07',aug:'08',sep:'09',oct:'10',nov:'11',dec:'12' };
    return `${m[3]}-${months[m[2]!.toLowerCase()] ?? '01'}-${m[1]!.padStart(2, '0')}`;
  }, ambiguous: false },
] as const;

export function parseDate(raw: string, forceMDY = true): string | null {
  const s = raw.trim();
  for (const fmt of DATE_FORMATS) {
    const m = s.match(fmt.re);
    if (m) {
      return fmt.parse(m as unknown as RegExpMatchArray);
    }
  }
  // EU: DD/MM/YYYY — only try if not forcing MDY
  if (!forceMDY) {
    const m = s.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (m) return `${m[3]}-${m[2]!.padStart(2, '0')}-${m[1]!.padStart(2, '0')}`;
  }
  return null;
}

function isAmbiguousDate(raw: string): boolean {
  const s = raw.trim();
  const m = s.match(/^(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{4})$/);
  if (!m) return false;
  const a = parseInt(m[1]!), b = parseInt(m[2]!);
  // Ambiguous if both a and b could be a month (1-12) and both could be a day
  return a <= 12 && b <= 12 && a !== b;
}

// ---------------------------------------------------------------------------
// Amount parsing
// ---------------------------------------------------------------------------

export function parseAmount(raw: string, decimalPlaces = 2): number | null {
  let s = raw.trim();
  // Strip currency symbols
  s = s.replace(/[$£€¥₩₹]/g, '').trim();
  // Strip parentheses (US negative notation)
  const negative = s.startsWith('(') && s.endsWith(')');
  if (negative) s = '-' + s.slice(1, -1);
  // Remove explicit negative sign and track
  const explicitNeg = s.startsWith('-');
  if (explicitNeg) s = s.slice(1);

  // Detect EU format: 1.234,56
  const euMatch = s.match(/^[\d.]+,\d{2}$/);
  if (euMatch) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else {
    // US format: remove commas (thousands separators)
    s = s.replace(/,/g, '');
  }

  const n = parseFloat(s);
  if (isNaN(n)) return null;
  const minor = Math.round(n * Math.pow(10, decimalPlaces));
  return (negative || explicitNeg) ? -minor : minor;
}

// ---------------------------------------------------------------------------
// Column header matching
// ---------------------------------------------------------------------------

function matchHeader(headers: string[], candidates: string[]): string | null {
  const lower = headers.map((h) => h.toLowerCase().trim());
  for (const c of candidates) {
    const idx = lower.findIndex((h) => h.includes(c.toLowerCase()));
    if (idx >= 0) return headers[idx]!;
  }
  return null;
}

export interface ColumnMapping {
  date: string | null;
  description: string | null;
  amount: string | null;
  debit: string | null;
  credit: string | null;
  balance: string | null;
  checkNum: string | null;
  dateFormat: 'MDY' | 'DMY' | 'unknown';
  flipSign: boolean;
}

// ---------------------------------------------------------------------------
// Known bank format presets
// ---------------------------------------------------------------------------

type BankPreset = {
  name: string;
  detect: (headers: string[]) => boolean;
  mapping: Partial<ColumnMapping>;
};

const BANK_PRESETS: BankPreset[] = [
  {
    name: 'Chase',
    detect: (h) => h.includes('Transaction Date') && h.includes('Post Date') && h.includes('Description') && h.includes('Category'),
    mapping: { date: 'Transaction Date', description: 'Description', amount: 'Amount', dateFormat: 'MDY', flipSign: false },
  },
  {
    name: 'Bank of America',
    detect: (h) => h.includes('Posted Date') && h.includes('Reference Number') && h.includes('Payee') && h.includes('Address'),
    mapping: { date: 'Posted Date', description: 'Payee', amount: 'Amount', dateFormat: 'MDY', flipSign: false },
  },
  {
    name: 'Capital One',
    detect: (h) => h.includes('Transaction Date') && h.includes('Card No.') && h.includes('Debit') && h.includes('Credit'),
    mapping: { date: 'Transaction Date', description: 'Description', debit: 'Debit', credit: 'Credit', dateFormat: 'MDY', flipSign: false },
  },
  {
    name: 'Apple Card',
    detect: (h) => h.includes('Amount (USD)') || (h.includes('Merchant') && h.includes('Clearing Date')),
    mapping: { date: 'Transaction Date', description: 'Description', amount: 'Amount (USD)', dateFormat: 'MDY', flipSign: false },
  },
  {
    name: 'Mint',
    detect: (h) => h.includes('Original Description') && h.includes('Transaction Type') && h.includes('Account Name'),
    mapping: { date: 'Date', description: 'Description', amount: 'Amount', dateFormat: 'MDY', flipSign: false },
  },
];

export function detectColumns(headers: string[]): ColumnMapping {
  // Try known presets first
  for (const preset of BANK_PRESETS) {
    if (preset.detect(headers)) {
      return {
        date: null, description: null, amount: null, debit: null, credit: null,
        balance: null, checkNum: null, dateFormat: 'MDY', flipSign: false,
        ...preset.mapping,
      };
    }
  }

  // Generic detection
  const date = matchHeader(headers, ['transaction date', 'trans date', 'date posted', 'posted date', 'date']);
  const description = matchHeader(headers, ['description', 'payee', 'merchant', 'narrative', 'details', 'memo', 'name']);
  const amount = matchHeader(headers, ['amount', 'transaction amount']);
  const debit = matchHeader(headers, ['debit', 'withdrawal', 'charges']);
  const credit = matchHeader(headers, ['credit', 'deposit', 'payments']);
  const balance = matchHeader(headers, ['balance', 'running balance']);
  const checkNum = matchHeader(headers, ['check', 'check #', 'check number', 'num', 'ref', 'reference']);

  return { date, description, amount, debit, credit, balance, checkNum, dateFormat: 'unknown', flipSign: false };
}

// ---------------------------------------------------------------------------
// Mint-specific: amount sign flip
// The Mint format exports debit amounts as positive with a 'Transaction Type' column
// ---------------------------------------------------------------------------

function adjustMintAmount(row: Row, amount: number): number {
  const txType = row['Transaction Type']?.toLowerCase() ?? '';
  if (txType === 'debit') return -Math.abs(amount);
  if (txType === 'credit') return Math.abs(amount);
  return amount;
}

// ---------------------------------------------------------------------------
// Main parser
// ---------------------------------------------------------------------------

export interface CsvParseOptions {
  accountId: string;
  currency?: string;
  mapping?: Partial<ColumnMapping>;
  dateFormatOverride?: 'MDY' | 'DMY';
  flipSign?: boolean;
}

export function parseCsv(
  content: string,
  options: CsvParseOptions,
): ImportResult {
  const errors: ImportError[] = [];
  const warnings: ImportWarning[] = [];

  // Strip UTF-8 BOM if present
  const cleaned = content.startsWith('﻿') ? content.slice(1) : content;

  const parsed = Papa.parse<Row>(cleaned, {
    header: true,
    skipEmptyLines: true,
  });

  for (const err of parsed.errors) {
    errors.push({ ...(err.row !== undefined ? { line: err.row } : {}), message: err.message });
  }

  const rows = parsed.data;
  if (rows.length === 0) {
    return {
      transactions: [], accounts: [], errors, warnings,
      format: 'csv', dateRange: { start: '', end: '' }, totalTransactions: 0,
    };
  }

  const headers = parsed.meta.fields ?? [];
  const autoMapping = detectColumns(headers);
  const mapping: ColumnMapping = { ...autoMapping, ...options.mapping };

  const forceMDY = (options.dateFormatOverride ?? mapping.dateFormat) !== 'DMY';
  const currency = options.currency ?? 'USD';
  const decimalPlaces = getCurrencyDecimalPlaces(currency);

  const isMint = BANK_PRESETS.find((p) => p.name === 'Mint')?.detect(headers) ?? false;

  const transactions: ImportedTransaction[] = [];
  let lineNum = 2; // 1-indexed, row 1 is header

  for (const row of rows) {
    // Date
    const rawDate = (mapping.date ? row[mapping.date] : null) ?? '';
    if (!rawDate.trim()) {
      errors.push({ line: lineNum, field: 'date', message: 'Missing date', rawData: JSON.stringify(row) });
      lineNum++;
      continue;
    }

    // Check for ambiguity before overriding
    if (mapping.dateFormat === 'unknown' && options.dateFormatOverride === undefined && isAmbiguousDate(rawDate)) {
      warnings.push({ line: lineNum, field: 'date', message: `Ambiguous date "${rawDate}" — assuming MM/DD/YYYY` });
    }

    const date = parseDate(rawDate, forceMDY);
    if (!date) {
      errors.push({ line: lineNum, field: 'date', message: `Cannot parse date: "${rawDate}"`, rawData: JSON.stringify(row) });
      lineNum++;
      continue;
    }

    // Amount
    let amountCents: number | null = null;
    if (mapping.amount) {
      const rawAmt = row[mapping.amount] ?? '';
      amountCents = parseAmount(rawAmt, decimalPlaces);
      if (amountCents === null) {
        errors.push({ line: lineNum, field: 'amount', message: `Cannot parse amount: "${rawAmt}"`, rawData: JSON.stringify(row) });
        lineNum++;
        continue;
      }
    } else if (mapping.debit && mapping.credit) {
      const rawDebit = row[mapping.debit]?.trim() ?? '';
      const rawCredit = row[mapping.credit]?.trim() ?? '';
      if (rawDebit) {
        const d = parseAmount(rawDebit, decimalPlaces);
        if (d === null) {
          errors.push({ line: lineNum, field: 'debit', message: `Cannot parse debit: "${rawDebit}"` });
          lineNum++;
          continue;
        }
        amountCents = -Math.abs(d);
      } else if (rawCredit) {
        const c = parseAmount(rawCredit, decimalPlaces);
        if (c === null) {
          errors.push({ line: lineNum, field: 'credit', message: `Cannot parse credit: "${rawCredit}"` });
          lineNum++;
          continue;
        }
        amountCents = Math.abs(c);
      } else {
        errors.push({ line: lineNum, message: 'Both debit and credit are empty' });
        lineNum++;
        continue;
      }
    } else {
      errors.push({ line: lineNum, message: 'No amount column mapped' });
      lineNum++;
      continue;
    }

    if (isMint) {
      amountCents = adjustMintAmount(row, amountCents);
    } else if (options.flipSign ?? mapping.flipSign) {
      amountCents = -amountCents;
    }

    const description = (mapping.description ? row[mapping.description] : null) ?? '';
    const num = mapping.checkNum ? (row[mapping.checkNum] ?? '') : '';

    transactions.push({
      date,
      description: description.trim(),
      amount: amountCents,
      currency,
      accountId: options.accountId,
      ...(num ? { num } : {}),
      rawData: row,
    });

    lineNum++;
  }

  const dates = transactions.map((t) => t.date).sort();
  return {
    transactions,
    accounts: [],
    errors,
    warnings,
    format: 'csv',
    dateRange: { start: dates[0] ?? '', end: dates[dates.length - 1] ?? '' },
    totalTransactions: transactions.length,
  };
}
